import {
  WSEmptyState,
  WSGrid,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import { Currency } from "@wingspanhq/wingspan-api-sdk";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import React from "react";
import { getHeroRangeSelectLabel } from "../../../shared/components/HeroRangeSelect/HeroRangeSelect";
import { useProfitAndLossQuery } from "../components/ProfitAndLoss/useProfitAndLossQuery";
import {
  CardAddTransactions,
  CardTaxes101,
  CardTrackPayments,
  CardYourAccounts,
  HelpCenterLink
} from "../components/SidebarCards";
import { useModalAddTransaction } from "../modals/ModalAddTransaction";

const money = (value?: string | number) =>
  formatMoney(Number(value ?? 0), Currency.USD);

export const RouteBookkeepingDashboard: React.FC = () => {
  const { query, range } = useProfitAndLossQuery();
  const modalAddTransaction = useModalAddTransaction();

  const renderHero = () => {
    if (query.isLoading) {
      return <WSText.Heading2 shimmer>Loading your summary…</WSText.Heading2>;
    }

    const income = Number(query.data?.totalIncome ?? 0);
    const expenses = Number(query.data?.totalExpenses ?? 0);

    if (income === 0 && expenses === 0) {
      return (
        <WSText.Heading2 color="gray600">
          No income or deductible expenses found for the current date range.{" "}
          <WSText.Heading2
            as="span"
            inline
            color="blue500"
            onClick={() => {
              void modalAddTransaction.open({});
            }}
          >
            Add transactions.
          </WSText.Heading2>
        </WSText.Heading2>
      );
    }

    return (
      <WSText.Heading2 color="gray600">
        {getHeroRangeSelectLabel([range.start, range.end]).full} you've made{" "}
        <WSText.Heading2 as="span" inline color="gray700">
          {money(income)}
        </WSText.Heading2>{" "}
        and we found{" "}
        <WSText.Heading2 as="span" inline color="gray700">
          {money(expenses)}
        </WSText.Heading2>{" "}
        in deductible expenses. 🎉
      </WSText.Heading2>
    );
  };

  return (
    <WSGrid gutter="2XL">
      <WSGrid.Item span={{ xs: "12", m: "8" }}>
        <WSList gap="2XL">
          {renderHero()}
          {query.isError ? (
            <WSEmptyState
              type="error"
              title="Couldn't load your summary"
              description="Something went wrong. Please try again."
              actions={{
                buttons: [
                  {
                    label: "Try again",
                    kind: "Secondary",
                    onClick: () => {
                      void query.refetch();
                    }
                  }
                ]
              }}
            />
          ) : null}
          <CardAddTransactions />
          <CardTrackPayments />
        </WSList>
      </WSGrid.Item>
      <WSGrid.Item span={{ xs: "12", m: "4" }}>
        <WSList gap="2XL">
          <CardYourAccounts />
          <CardTaxes101 />
          <HelpCenterLink />
        </WSList>
      </WSGrid.Item>
    </WSGrid>
  );
};
